// Strings em portugues brasileiro — locale primario.
//
// Convencao:
//   - Estrutura por dominio/feature: `common`, `nav`, `auth`, `transactions`...
//   - Chaves em camelCase para alinhar ao acesso JS (`t('auth.login.title')`).
//   - Mensagens curtas para mobile; expandir em descricao por contexto.
//   - Erros tem 2 niveis: `error.generic` (catch-all) + erros especificos
//     por feature.
//
// Quando adicionar nova chave: replicar shape em `en.ts` (placeholder OK).
// O type `Translations` (inferido daqui) propaga via `types.d.ts` para
// autocompletion type-safe em `t()`.

export const ptBR = {
  common: {
    save: 'Salvar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    confirm: 'Confirmar',
    close: 'Fechar',
    back: 'Voltar',
    next: 'Avançar',
    loading: 'Carregando...',
    retry: 'Tentar de novo',
    search: 'Buscar',
    error: {
      generic: 'Algo deu errado. Tente novamente.',
      network: 'Sem conexão. Verifique sua internet.',
      unauthorized: 'Sua sessão expirou. Faça login novamente.',
      notFound: 'Não encontramos o que você procurava.',
      validation: 'Verifique os campos preenchidos.',
    },
    offline: 'Você está offline. Algumas ações podem ficar indisponíveis.',
    appName: 'Grana Money',
  },
  nav: {
    home: 'Início',
    groups: 'Grupos',
    reports: 'Relatórios',
    profile: 'Perfil',
    notifications: 'Notificações',
    transactions: 'Transações',
    expenses: 'Despesas',
    incomes: 'Receitas',
    budgets: 'Orçamentos',
    forecasts: 'Previsões',
    settings: 'Configurações',
    logout: 'Sair',
  },
  auth: {
    welcome: {
      brand: 'Grana Money',
      headline: 'Comece agora. Suas finanças, sob controle.',
      subhead: 'Crie sua conta em menos de 1 minuto e convide seu grupo familiar.',
      ctaPrimary: 'Criar conta grátis',
      ctaSecondary: 'Já tenho conta · Entrar',
      slides: {
        s1Tag: 'CAPÍTULO 01',
        s1Title: 'Dinheiro que respira junto.',
        s1Sub:
          'O Grana Money une suas finanças pessoais às do grupo familiar numa única visão. Sem planilha, sem fricção.',
        s2Tag: 'CAPÍTULO 02',
        s2Title: 'Toda entrada importa.',
        s2Sub:
          'Registre receitas em segundos e veja seu saldo crescer. Visão clara de onde cada real está.',
        s3Tag: 'CAPÍTULO 03',
        s3Title: 'Antecipe, não seja surpreso.',
        s3Sub:
          'Relatórios e projeções mantêm você um passo à frente. Um orçamento que se adapta ao seu ritmo.',
      },
      pillars: {
        secure: 'Seguro',
        secureDesc: 'Criptografia de ponta a ponta',
        free: 'Gratuito',
        freeDesc: 'Plano base sem custos',
      },
      skip: 'Pular',
    },
    login: {
      title: 'Entrar',
      subtitle: 'Bem-vindo de volta. Continue de onde parou.',
      email: 'E-mail',
      password: 'Senha',
      submit: 'Entrar',
      forgot: 'Esqueci minha senha',
      noAccount: 'Não tem conta?',
      registerCta: 'Criar conta',
      rememberMe: 'Lembrar de mim',
      orContinueWith: 'ou continue com',
      googleCta: 'Google · em breve',
      appleCta: 'Apple · em breve',
      socialSoon: 'Login social estará disponível em breve.',
      errors: {
        invalid: 'E-mail ou senha inválidos.',
        rateLimited: 'Muitas tentativas. Tente novamente em alguns minutos.',
        accountDisabled: 'Esta conta está desativada. Entre em contato com o suporte.',
      },
    },
    register: {
      title: 'Criar conta',
      stepLabel: 'Passo {{current}} de {{total}}',
      name: 'Nome completo',
      namePlaceholder: 'Como podemos te chamar?',
      email: 'E-mail',
      emailPlaceholder: 'voce@exemplo.com',
      password: 'Senha',
      passwordPlaceholder: 'Crie uma senha forte',
      confirmPassword: 'Confirme a senha',
      submit: 'Criar conta',
      hasAccount: 'Já tem conta?',
      loginCta: 'Entrar',
      next: 'Avançar',
      back: 'Voltar',
      stepHints: {
        s1: 'Use seu nome real para personalizar a experiência do grupo.',
        s2: 'Vamos enviar um link de verificação para esse e-mail.',
        s3: 'Sua senha precisa atender às regras abaixo.',
      },
      errors: {
        emailExists: 'Este e-mail já está cadastrado.',
      },
    },
    forgotPassword: {
      title: 'Recuperar senha',
      description: 'Informe o e-mail cadastrado e enviaremos as instruções.',
      method: 'Como prefere receber?',
      methodEmail: 'E-mail',
      methodSms: 'SMS',
      submit: 'Enviar instruções',
      backToLogin: 'Voltar ao login',
      success: 'Se houver conta cadastrada, você receberá uma mensagem em breve.',
    },
    resetPassword: {
      title: 'Redefinir senha',
      description: 'Crie uma senha nova e segura para a sua conta.',
      tokenLabel: 'Código',
      tokenPlaceholder: 'Cole aqui o código recebido',
      tokenVerified: 'Link verificado',
      tokenManualHint: 'Não recebeu o link? Cole o código manualmente.',
      newPassword: 'Nova senha',
      newPasswordPlaceholder: 'Crie uma senha forte',
      submit: 'Redefinir senha',
      success: 'Senha redefinida com sucesso.',
      errors: {
        invalidToken: 'Link inválido ou expirado.',
      },
    },
  },
  transactions: {
    new: 'Nova transação',
    expense: 'Despesa',
    income: 'Receita',
    amount: 'Valor',
    description: 'Descrição',
    category: 'Categoria',
    date: 'Data',
  },
};

// Inferimos o tipo a partir do shape do `pt-BR` (sem `as const`) para que
// `en.ts` possa atribuir strings diferentes mantendo a mesma estrutura.
// Guard estatico: se uma chave faltar em qualquer locale, TypeScript reclama.
export type Translations = typeof ptBR;
